import {
  EDITABLE_MESSAGE_STATUSES,
  MESSAGE_STATUS_META,
  RECIPIENT_STATUS_META,
  SENDABLE_MESSAGE_STATUSES,
  STOPPABLE_MESSAGE_STATUSES,
} from "./mailToolConstants";

export const getValue = (item, ...keys) => {
  if (!item) return undefined;
  return keys.map((key) => item[key]).find((value) => value !== undefined);
};

export const readResponseData = (response, fallback = null) => {
  if (response && typeof response === "object") {
    const hasData =
      Object.prototype.hasOwnProperty.call(response, "data") ||
      Object.prototype.hasOwnProperty.call(response, "Data");

    if (hasData) return getValue(response, "data", "Data") ?? fallback;
  }

  return response ?? fallback;
};

export const isErrorResponse = (response) => {
  if (!response || typeof response !== "object") return false;

  const status = String(getValue(response, "status", "Status") || "")
    .trim()
    .toLowerCase();

  return (
    status === "error" ||
    getValue(response, "succeeded", "Succeeded", "succeded") === false
  );
};

export const isSuccessResponse = (response) => {
  if (!response || typeof response !== "object") return false;

  const status = String(getValue(response, "status", "Status") || "")
    .trim()
    .toLowerCase();

  return (
    status === "success" &&
    getValue(response, "succeeded", "Succeeded", "succeded") === true
  );
};

export const getResponseMessage = (response, fallback = "Η ενέργεια απέτυχε") =>
  getValue(response, "message", "Message") ||
  getValue(response?.response, "message", "Message") ||
  getValue(response?.data, "message", "Message") ||
  fallback;

export const getOptionLabel = (item) =>
  getValue(
    item,
    "label",
    "Label",
    "description",
    "Description",
    "name",
    "Name",
    "abbr",
    "Abbr"
  ) ||
  getValue(item, "year", "Year", "code", "Code", "id", "Id") ||
  "";

export const getOptionValue = (item) =>
  getValue(item, "id", "Id", "value", "Value");

export const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("el-GR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const normalizeNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeNumberArray = (value) => {
  const values = Array.isArray(value) ? value : [value];
  return [
    ...new Set(
      values
        .map(normalizeNumber)
        .filter((item) => item !== null)
    ),
  ];
};

const normalizeBoolean = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  if (typeof value === "boolean") return value;
  return String(value).toLowerCase() === "true";
};

const hasFilterValue = (value) =>
  Array.isArray(value) ? value.length > 0 : value !== null;

export const normalizeFiltersForApi = (filters, includeEmpty = true) => {
  const next = {
    seasonIds: normalizeNumberArray(filters?.seasonIds ?? filters?.seasonId),
    applicationTypeIds: normalizeNumberArray(
      filters?.applicationTypeIds ?? filters?.applicationTypeId
    ),
    applicationStatusIds: normalizeNumberArray(
      filters?.applicationStatusIds ?? filters?.applicationStatusId
    ),
    genderId: normalizeNumber(filters?.genderId),
    emailConfirmed: normalizeBoolean(filters?.emailConfirmed),
    search: filters?.search?.trim() || null,
  };

  if (includeEmpty) return next;

  return Object.values(next).some(hasFilterValue) ? next : null;
};

export const normalizeFiltersForForm = (filters) => {
  const emailConfirmed = getValue(
    filters,
    "emailConfirmed",
    "EmailConfirmed"
  );

  return {
    seasonIds: normalizeNumberArray(
      getValue(filters, "seasonIds", "SeasonIds") ??
        getValue(filters, "seasonId", "SeasonId")
    ),
    applicationTypeIds: normalizeNumberArray(
      getValue(filters, "applicationTypeIds", "ApplicationTypeIds") ??
        getValue(filters, "applicationTypeId", "ApplicationTypeId")
    ),
    applicationStatusIds: normalizeNumberArray(
      getValue(filters, "applicationStatusIds", "ApplicationStatusIds") ??
        getValue(filters, "applicationStatusId", "ApplicationStatusId")
    ),
    genderId: getValue(filters, "genderId", "GenderId") ?? "",
    emailConfirmed:
      emailConfirmed === null || emailConfirmed === undefined
        ? ""
        : String(emailConfirmed),
    search: getValue(filters, "search", "Search") || "",
  };
};

export const getRecipientKey = (recipient) =>
  recipient?.recipientUserId || recipient?.id || recipient?.email || recipient?.to;

export const normalizeRecipientCandidate = (recipient, selected = true) => {
  const recipientUserId = getValue(
    recipient,
    "recipientUserId",
    "RecipientUserId",
    "id",
    "Id"
  );

  return {
    id: recipientUserId,
    applicationId: getValue(recipient, "applicationId", "ApplicationId"),
    protocolNumber: getValue(recipient, "protocolNumber", "ProtocolNumber"),
    seasonId: getValue(recipient, "seasonId", "SeasonId"),
    applicationTypeId: getValue(
      recipient,
      "applicationTypeId",
      "ApplicationTypeId"
    ),
    applicationStatusId: getValue(
      recipient,
      "applicationStatusId",
      "ApplicationStatusId"
    ),
    genderId: getValue(recipient, "genderId", "GenderId"),
    recipientPersonId: getValue(recipient, "recipientPersonId", "RecipientPersonId"),
    recipientUserId,
    email: getValue(recipient, "email", "Email", "to", "To") || "",
    emailConfirmed: getValue(recipient, "emailConfirmed", "EmailConfirmed"),
    displayName:
      getValue(recipient, "displayName", "DisplayName") ||
      getValue(recipient, "email", "Email", "to", "To") ||
      "",
    amka: getValue(recipient, "amka", "AMKA"),
    afm: getValue(recipient, "afm", "AFM"),
    selected,
  };
};

export const normalizeSavedRecipient = (recipient) => {
  const recipientUserId = getValue(
    recipient,
    "recipientUserId",
    "RecipientUserId"
  );

  return {
    id: recipientUserId,
    applicationId: getValue(recipient, "applicationId", "ApplicationId"),
    protocolNumber: getValue(recipient, "protocolNumber", "ProtocolNumber"),
    applicationTypeId: getValue(
      recipient,
      "applicationTypeId",
      "ApplicationTypeId"
    ),
    recipientPersonId: getValue(
      recipient,
      "recipientPersonId",
      "RecipientPersonId"
    ),
    recipientUserId,
    email: getValue(recipient, "to", "To", "email", "Email") || "",
    emailConfirmed: getValue(recipient, "emailConfirmed", "EmailConfirmed"),
    displayName:
      getValue(recipient, "displayName", "DisplayName") ||
      getValue(recipient, "to", "To", "email", "Email") ||
      "",
    selected: Boolean(recipientUserId),
  };
};

export const normalizeEmailMessage = (email) => ({
  id: getValue(email, "id", "Id"),
  subject: getValue(email, "subject", "Subject") || "",
  body: getValue(email, "body", "Body") || "",
  statusId: getValue(email, "statusId", "StatusId"),
  createdAt: getValue(email, "createdAt", "CreatedAt"),
  sentAt: getValue(email, "sentAt", "SentAt"),
  recipientsTotal: getValue(email, "recipientsTotal", "RecipientsTotal") || 0,
  recipientsSent: getValue(email, "recipientsSent", "RecipientsSent") || 0,
  recipientsFailed: getValue(email, "recipientsFailed", "RecipientsFailed") || 0,
});

export const normalizeEmailDetail = (email) => {
  const normalized = normalizeEmailMessage(email);
  const recipients = getValue(email, "recipients", "Recipients") || [];
  const filters = getValue(email, "filters", "Filters");

  return {
    ...normalized,
    filters: normalizeFiltersForForm(filters),
    recipients: recipients.map((recipient) => ({
      ...normalizeSavedRecipient(recipient),
      statusId: getValue(recipient, "statusId", "StatusId"),
      retries: getValue(recipient, "retries", "Retries") || 0,
      sentAt: getValue(recipient, "sentAt", "SentAt"),
      lastError: getValue(recipient, "lastError", "LastError"),
    })),
  };
};

export const getSelectedRecipientUserIds = (recipients) => {
  const ids = recipients
    .filter((recipient) => recipient.selected)
    .map((recipient) => recipient.recipientUserId || recipient.id)
    .filter(Boolean);

  return [...new Set(ids)];
};

export const getMessageStatusMeta = (statusId) =>
  MESSAGE_STATUS_META[statusId] || { label: `Κατάσταση ${statusId}`, color: "default" };

export const getRecipientStatusMeta = (statusId) =>
  RECIPIENT_STATUS_META[statusId] || {
    label: statusId ? `Κατάσταση ${statusId}` : "Επιλεγμένος",
    color: "default",
  };

export const isEditableStatus = (statusId, isNew) =>
  isNew || EDITABLE_MESSAGE_STATUSES.includes(statusId);

export const isSendableStatus = (statusId, isNew) =>
  isNew || SENDABLE_MESSAGE_STATUSES.includes(statusId);

export const isStoppableStatus = (statusId) =>
  STOPPABLE_MESSAGE_STATUSES.includes(statusId);
